import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { listHealthProducts } from "@/lib/cms/health-products.functions";
import { HealthProductCard } from "@/components/health-products/ProductCard";

export const healthProductsQuery = queryOptions({
  queryKey: ["health-products", "list"],
  queryFn: () => listHealthProducts(),
  staleTime: 60_000,
});

const TITLE = "Health Products | Poonam Kapur Healthy Kitchen";
const DESC = "Shop wellness products and pantry staples from Poonam Kapur Healthy Kitchen, Mumbai.";

export const Route = createFileRoute("/health-products/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://poonamkapur.com/health-products" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://poonamkapur.com/health-products" }],
  }),
  component: HealthProductsPage,
});

function HealthProductsPage() {
  const { data, isLoading, isError, refetch } = useQuery(healthProductsQuery);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string | null>(null);

  const products = data ?? [];
  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category).filter((c): c is string => !!c))).sort(),
    [products],
  );
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter(
      (p) =>
        (!cat || p.category === cat) &&
        (!needle ||
          [p.name, p.note, p.brand, p.category, p.productType].some((v) => v?.toLowerCase().includes(needle))),
    );
  }, [products, q, cat]);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Health Products"
        title="Wellness, from our kitchen to yours."
        intro="Products we trust and use ourselves."
      />
      <Section className="pt-0">
        {isLoading ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center" role="status">
            <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
            <p className="mt-4 text-sm text-muted-foreground">Loading products…</p>
          </div>
        ) : isError ? (
          <div className="rounded-3xl border border-dashed border-destructive/30 bg-destructive/5 p-12 text-center">
            <h2 className="font-display text-xl">We couldn't load products</h2>
            <p className="mt-2 text-muted-foreground">Please check your connection and try again.</p>
            <button className="mt-4 text-sm underline" onClick={() => refetch()}>Try again</button>
          </div>
        ) : products.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-12 text-center">
            <h2 className="font-display text-xl">Our shop is being prepared</h2>
            <p className="mt-2 text-muted-foreground">We're adding prices and product details. Products will appear here as soon as they're ready to order.</p>
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3"><div className="relative md:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="pl-9" aria-label="Search products" />
              </div><Link to="/health-products/cart" className="whitespace-nowrap text-sm underline">View cart</Link></div>
              {categories.length > 0 ? (
                <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
                  {[null, ...categories].map((c) => (
                    <button
                      key={c ?? "all"}
                      onClick={() => setCat(c)}
                      aria-pressed={cat === c}
                      className={cn(
                        "rounded-full border px-4 py-1.5 text-sm transition-colors",
                        cat === c ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {c ?? "All"}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            {visible.length === 0 ? (
              <p className="py-12 text-center text-muted-foreground">No products match your search.</p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((p) => <HealthProductCard key={p.id} p={p} />)}
              </div>
            )}
          </>
        )}
      </Section>
    </SiteLayout>
  );
}
